import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { apiGetShortUrl } from 'api/shortUrl/apiShortUrl';
import { ApiShortUrlType } from 'api/shortUrl/types/ApiShortUrlType';
import { Icon } from 'components/Icon';
import { colors, fontWeights } from 'theme/theme';

import { StyledClipboardButton } from './ShareStyleButton';
import { makeParameters, withSocialUtm } from './helpers';

const BUTTON_SIZE = 48;
const MESSAGE_OFFSET = 28;
const POPUP_MARGIN = 100;

const CopiedMessage = styled.div`
  align-items: center;
  background-color: ${colors.brandBlue};
  border-radius: 8px;
  color: ${colors.white};
  display: flex;
  font-size: 16px;
  font-weight: ${fontWeights.semiBold};
  gap: 8px;
  left: ${MESSAGE_OFFSET}px;
  padding: 4px 8px;
  position: absolute;
  top: ${MESSAGE_OFFSET}px;
  width: max-content;

  &.left {
    left: auto;
    right: ${MESSAGE_OFFSET}px;
  }
`;

const Container = styled.div`
  display: flex;
  position: relative;
  height: ${BUTTON_SIZE}px;
  width: ${BUTTON_SIZE}px;
`;

type Props = {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  object: Record<string, any>;
  objectType: ApiShortUrlType;
  utmParams: Record<string, string>;
};

export function CopyClipboardButton({ object, objectType, utmParams }: Props) {
  const [shortUrl, setShortUrl] = useState<string>();
  const [showCopied, setShowCopied] = useState(false);
  const [showLeft, setShowLeft] = useState(false);

  const onSuccess = () => {
    setShowCopied(true);

    setTimeout(() => {
      setShowCopied(false);
    }, 5000);
  };

  useEffect(() => {
    apiGetShortUrl({
      toType: objectType,
      toId: object.id,
      toUrl: object.url ? object.url[CURRENT_LOCALE] : null,
      extraParams: makeParameters(withSocialUtm(utmParams, 'copy')),
    }).then((url) => setShortUrl(url));
  }, [objectType, object.id, object.url, utmParams]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = useCallback((node: any) => {
    if (
      node &&
      window &&
      node.getBoundingClientRect().right + POPUP_MARGIN > window.innerWidth
    ) {
      setShowLeft(true);
    } else {
      setShowLeft(false);
    }
  }, []);

  if (!shortUrl) return null;

  return (
    <Container ref={ref}>
      {showCopied && (
        <CopiedMessage className={showLeft ? 'left' : ''}>
          <Icon name="simple-check" colorName="white" size={16} />
          {getText('Link copied')}
        </CopiedMessage>
      )}

      <StyledClipboardButton
        data-clipboard-text={shortUrl}
        onSuccess={onSuccess}
        title={getText('Copy Link')}
        aria-label={getText('Copy Link')}
      >
        <Icon name="link" colorName="white" size={25} />
      </StyledClipboardButton>
    </Container>
  );
}
